import { Fragment, useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { logAudit } from '../../lib/audit'
import type { Company } from '../../types'

type CompanyRow = Pick<Company, 'id' | 'name' | 'active' | 'admin_email' | 'created_at'>

interface AuditRow {
  id: string
  actor_email: string | null
  action: string
  target_type: string | null
  target_id: string | null
  payload: Record<string, unknown> | null
  created_at: string
}

export default function AdminPage() {
  const [companies, setCompanies] = useState<CompanyRow[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [msg, setMsg] = useState<{ id: string; text: string; ok: boolean } | null>(null)
  const [auditRows, setAuditRows] = useState<AuditRow[]>([])
  const [auditLoading, setAuditLoading] = useState(true)

  useEffect(() => {
    fetchCompanies()
    fetchAudit()
  }, [])

  async function fetchAudit() {
    setAuditLoading(true)
    const { data } = await supabase
      .from('audit_log')
      .select('id, actor_email, action, target_type, target_id, payload, created_at')
      .order('created_at', { ascending: false })
      .limit(50)
    setAuditRows((data ?? []) as AuditRow[])
    setAuditLoading(false)
  }

  async function fetchCompanies() {
    setLoading(true)
    const { data } = await supabase
      .from('companies')
      .select('id, name, active, admin_email, created_at')
      .order('created_at', { ascending: false })
    setCompanies((data ?? []) as CompanyRow[])
    setLoading(false)
  }

  async function setActive(company: CompanyRow, active: boolean) {
    setActionLoading(company.id)
    setMsg(null)

    const { error } = await supabase
      .from('companies')
      .update({ active })
      .eq('id', company.id)

    if (error) {
      setMsg({ id: company.id, text: error.message, ok: false })
      setActionLoading(null)
      return
    }

    // Audit log (best-effort, não bloqueia)
    void logAudit(active ? 'company.activate' : 'company.deactivate', {
      target_type: 'company',
      target_id: company.id,
      payload: { name: company.name, admin_email: company.admin_email },
    })

    // Atualiza localmente
    setCompanies(prev => prev.map(c => c.id === company.id ? { ...c, active } : c))

    // Dispara e-mail de liberação
    if (active && company.admin_email) {
      const { error: emailErr } = await supabase.functions.invoke('send-access-email', {
        body: { to: company.admin_email, companyName: company.name },
      })
      if (emailErr) {
        setMsg({ id: company.id, text: `Acesso liberado, mas e-mail falhou: ${emailErr.message}`, ok: false })
      } else {
        setMsg({ id: company.id, text: `Acesso liberado! E-mail enviado para ${company.admin_email}`, ok: true })
      }
    } else {
      setMsg({ id: company.id, text: active ? 'Acesso liberado.' : 'Acesso suspenso.', ok: true })
    }

    setActionLoading(null)
  }

  const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('pt-BR')

  return (
    <div className="min-h-screen bg-paper p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-ink">Painel Admin</h1>
            <p className="text-ink-faint text-sm mt-1">Gerencie o acesso das empresas clientes</p>
          </div>
          <button
            onClick={fetchCompanies}
            className="px-4 py-2 bg-sunken hover:bg-line text-ink-muted text-sm rounded-lg transition-colors"
          >
            Atualizar
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-surface border border-line rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-xs text-ink-faint uppercase tracking-wider">
                  <th className="text-left px-5 py-3">Empresa</th>
                  <th className="text-left px-5 py-3">E-mail</th>
                  <th className="text-left px-5 py-3">Cadastro</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3">Ação</th>
                </tr>
              </thead>
              <tbody>
                {companies.map(c => (
                  <Fragment key={c.id}>
                    <tr className="border-b border-line hover:bg-sunken/50 transition-colors">
                      <td className="px-5 py-3 text-ink font-medium">{c.name}</td>
                      <td className="px-5 py-3 text-ink-muted">{c.admin_email ?? '—'}</td>
                      <td className="px-5 py-3 text-ink-faint">{fmtDate(c.created_at)}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                          c.active
                            ? 'bg-brand-600/10 text-brand-700 dark:text-brand-300 border border-brand-600/30'
                            : 'bg-danger/10 text-danger border border-danger/30'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${c.active ? 'bg-green-400' : 'bg-red-400'}`} />
                          {c.active ? 'Ativo' : 'Suspenso'}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        {c.active ? (
                          <button
                            onClick={() => setActive(c, false)}
                            disabled={actionLoading === c.id}
                            className="px-3 py-1.5 bg-danger/10 hover:bg-danger/20 border border-danger/30 text-danger text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                          >
                            {actionLoading === c.id ? '...' : 'Suspender'}
                          </button>
                        ) : (
                          <button
                            onClick={() => setActive(c, true)}
                            disabled={actionLoading === c.id}
                            className="px-3 py-1.5 bg-brand-600/10 hover:bg-brand-600/20 border border-brand-600/30 text-brand-700 dark:text-brand-300 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                          >
                            {actionLoading === c.id ? '...' : 'Ativar'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {msg?.id === c.id && (
                      <tr className="border-b border-line">
                        <td colSpan={5} className={`px-5 py-2 text-xs ${msg.ok ? 'text-brand-700 dark:text-brand-300' : 'text-danger'}`}>
                          {msg.text}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {companies.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-10 text-center text-ink-faint">
                      Nenhuma empresa encontrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Auditoria ─────────────────────────────────────────────── */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-semibold text-ink">Auditoria</h2>
              <p className="text-xs text-ink-faint">Últimos 50 eventos sensíveis registrados na plataforma</p>
            </div>
            <button
              onClick={fetchAudit}
              className="px-3 py-1.5 bg-sunken hover:bg-line text-ink-muted text-xs rounded-lg transition-colors"
            >
              Atualizar
            </button>
          </div>

          {auditLoading ? (
            <div className="flex justify-center py-10">
              <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="bg-surface border border-line rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-line text-[10px] text-ink-faint uppercase tracking-wider">
                    <th className="text-left px-4 py-2.5 whitespace-nowrap">Quando</th>
                    <th className="text-left px-4 py-2.5">Ator</th>
                    <th className="text-left px-4 py-2.5">Ação</th>
                    <th className="text-left px-4 py-2.5">Alvo</th>
                    <th className="text-left px-4 py-2.5">Detalhes</th>
                  </tr>
                </thead>
                <tbody>
                  {auditRows.map(r => (
                    <tr key={r.id} className="border-b border-line hover:bg-sunken/50">
                      <td className="px-4 py-2 text-ink-muted whitespace-nowrap font-mono">
                        {new Date(r.created_at).toLocaleString('pt-BR')}
                      </td>
                      <td className="px-4 py-2 text-ink-muted">{r.actor_email ?? '—'}</td>
                      <td className="px-4 py-2">
                        <code className="px-1.5 py-0.5 rounded bg-sunken text-brand-700 dark:text-brand-300 text-[11px]">{r.action}</code>
                      </td>
                      <td className="px-4 py-2 text-ink-muted">
                        {r.target_type ? `${r.target_type}: ${r.target_id?.slice(0, 8) ?? '—'}` : '—'}
                      </td>
                      <td className="px-4 py-2 text-ink-faint font-mono text-[10px] max-w-xs truncate">
                        {r.payload ? JSON.stringify(r.payload) : '—'}
                      </td>
                    </tr>
                  ))}
                  {auditRows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-ink-faint">
                        Nenhum evento registrado ainda.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
